import {
  IconButton as ChakraUIIconButton,
  type IconButtonProps as ChakraIconButtonProps
} from "@chakra-ui/react";
import { forwardRef, type ReactNode, type Ref } from "react";

export type IconButtonProps = Omit<
  ChakraIconButtonProps,
  "size" | "variant"
> & {
  destructive?: boolean;
  elevated?: boolean;
  round?: boolean;
  icon?: ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  size?:
    | ChakraIconButtonProps["size"]
    | string
    | Record<string, string>
    | Array<string | null>;
  variant?: string;
  ref?: Ref<HTMLButtonElement>;
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      children,
      round,
      elevated,
      destructive,
      variant,
      size,
      w,
      h,
      width,
      height,
      minW,
      minH,
      p,
      px,
      py,
      css: cssProp,
      ...props
    },
    ref
  ) {
    const resolvedVariant = (
      destructive && variant ? `destructive-${variant}` : variant
    ) as ChakraIconButtonProps["variant"];

    const hasExplicitDimensions =
      w != null || h != null || width != null || height != null;

    const isRound = round !== false;

    return (
      <ChakraUIIconButton
        ref={ref}
        variant={resolvedVariant}
        size={
          hasExplicitDimensions
            ? undefined
            : (size as ChakraIconButtonProps["size"])
        }
        boxShadow={elevated ? "lg" : "none"}
        borderRadius={isRound ? "50%" : undefined}
        aspectRatio={isRound ? "1" : undefined}
        w={w ?? width}
        h={h ?? height}
        minW={hasExplicitDimensions ? (minW ?? "unset") : minW}
        minH={hasExplicitDimensions ? (minH ?? "unset") : minH}
        p={p}
        px={px}
        py={py}
        css={cssProp as object | undefined}
        {...(props as ChakraIconButtonProps)}
      >
        {icon ?? children}
      </ChakraUIIconButton>
    );
  }
);

export { IconButton };
export type { IconButtonProps as ExtIconButtonProps };
